/**
 * F-JP-28 — attachment integrity, in the order the spec fixes it.
 *
 *   existence → streaming SHA-256 (1 MiB chunks) → byte count → digest
 *
 * The order is user-facing. A missing file reported as a hash mismatch sends
 * someone looking for corruption in a file that is not there, and a file
 * checked by size alone passes while holding a gigabyte of the wrong bytes.
 *
 * This lives in `@ff/store` rather than in the attachment packet because
 * `verifyAttachments` runs before **every** entry and draft write, and the
 * repository cannot be made to depend on a packet that depends on it. What P18
 * owns is the port; what this owns is the verification the write path needs,
 * and the repository accepts either — a {@link JournalAttachmentVerifier} is a
 * function, so the full `JournalAttachmentPort.verify` can be handed in
 * unchanged once it exists.
 */

import type {
  JournalAttachment,
  JournalAttachmentIntegrity,
  JournalRepositoryErrorCode,
} from "@ff/domain";
import type { DigestPort } from "@ff/domain/ports";

import type { JournalFileStore } from "./files";
import { journalAttachmentPath } from "./layout";

/** 1 MiB, the chunk size F-JP-28 names. */
export const JOURNAL_DIGEST_CHUNK_BYTES = 1024 * 1024;

export type JournalAttachmentVerifier = (
  attachment: JournalAttachment,
) => Promise<JournalAttachmentIntegrity>;

/**
 * Splits a buffer into the chunks the digest port consumes.
 *
 * Chunking a buffer we already hold looks pointless until the substrate is a
 * real one: the port's contract is that chunk boundaries cannot change the
 * digest, and the only way that stays true is if every caller feeds it the same
 * way. A whole-buffer caller here would be the one that stops matching when the
 * OPFS adapter streams.
 */
export async function* journalDigestChunks(
  bytes: Uint8Array,
): AsyncIterable<Uint8Array> {
  for (let offset = 0; offset < bytes.byteLength; offset += JOURNAL_DIGEST_CHUNK_BYTES) {
    yield bytes.subarray(offset, offset + JOURNAL_DIGEST_CHUNK_BYTES);
  }
}

export function createJournalAttachmentVerifier(
  files: JournalFileStore,
  digest: DigestPort,
): JournalAttachmentVerifier {
  return async (attachment) => {
    const path = journalAttachmentPath(attachment.ownerEntryID, attachment.id);

    let read;
    try {
      read = await files.read(path);
    } catch {
      // The store itself is not answering. That is not evidence about this
      // file, and calling it `missing` would let a sweep reclaim a healthy one.
      return "unavailable";
    }
    if (read.kind === "missing") return "missing";
    if (read.kind === "failed") return "unavailable";

    const found = await digest.sha256(journalDigestChunks(read.bytes));
    if (read.bytes.byteLength !== attachment.byteCount) return "byteCountMismatch";
    if (found !== attachment.contentSHA256) return "hashMismatch";
    return "verified";
  };
}

/**
 * Runs before every entry and draft write, mapping integrity to the error the
 * repository reports (F-JP-28): missing → `attachmentMissing`, either mismatch
 * → `attachmentCorrupt`.
 *
 * Attachments are checked in stored order, which is ascending id, so the error
 * a user sees is stable across runs rather than a race between two bad files.
 */
export async function verifyJournalAttachments(
  verify: JournalAttachmentVerifier,
  attachments: readonly JournalAttachment[],
): Promise<JournalRepositoryErrorCode | undefined> {
  for (const attachment of attachments) {
    const integrity = await verify(attachment);
    switch (integrity) {
      case "verified":
        continue;
      case "missing":
        return "attachmentMissing";
      case "byteCountMismatch":
      case "hashMismatch":
        return "attachmentCorrupt";
      case "unavailable":
        return "repositoryUnavailable";
    }
  }
  return undefined;
}
